import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Plus, ChevronLeft } from "lucide-react";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersAdminApi, ALL_ROLES, type UserAdminRow } from "@/lib/users-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const KEY = ["admin", "users"] as const;

function RoleChecks({ value, onToggle }: { value: Set<string>; onToggle: (r: string) => void }) {
  return (
    <div className="grid grid-cols-2 gap-1">
      {ALL_ROLES.map((r) => (
        <label key={r} className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={value.has(r)} onChange={() => onToggle(r)} />
          {r}
        </label>
      ))}
    </div>
  );
}

function PasswordReveal({ password }: { password: string }) {
  return (
    <div className="rounded border border-amber-500 bg-amber-50 p-3 text-sm dark:bg-amber-950">
      <p className="font-medium">Generated password</p>
      <div className="mt-1 flex items-center gap-2">
        <code className="rounded bg-background px-2 py-1 font-mono">{password}</code>
        <Button size="sm" variant="outline" onClick={() => void navigator.clipboard?.writeText(password)}>
          Copy
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Copy it now — it won&apos;t be shown again.</p>
    </div>
  );
}

export function UsersAdminPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const page = 0;
  const [editing, setEditing] = useState<UserAdminRow | null>(null);
  const [draftRoles, setDraftRoles] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRoles, setNewRoles] = useState<Set<string>>(new Set());
  const [revealed, setRevealed] = useState<string | null>(null);
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  const usersQ = useQuery({ queryKey: [...KEY, q, page], queryFn: () => usersAdminApi.list(q, page) });
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const setRoles = useMutation({
    mutationFn: (row: UserAdminRow) => usersAdminApi.setRoles(row.id, [...draftRoles]),
    onSuccess: async () => { await invalidate(); setEditing(null); },
  });
  const toggleLock = useMutation({
    mutationFn: (row: UserAdminRow) => (row.lockedOut ? usersAdminApi.unlock(row.id) : usersAdminApi.lock(row.id)),
    onSuccess: () => invalidate(),
  });
  const resetPw = useMutation({
    mutationFn: (row: UserAdminRow) => usersAdminApi.resetPassword(row.id),
    onSuccess: (res) => {
      if (res && res.generatedPassword) { setRevealed(res.generatedPassword); setResetMsg(null); }
      else { setRevealed(null); setResetMsg("Password reset — a reset link was emailed to the user."); }
    },
  });
  const create = useMutation({
    mutationFn: () => usersAdminApi.create(newEmail, [...newRoles]),
    onSuccess: async (res) => {
      await invalidate();
      setRevealed(res.generatedPassword);
      setCreating(false);
      setNewEmail("");
      setNewRoles(new Set());
    },
  });

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, r: string) => {
    const next = new Set(set);
    if (next.has(r)) next.delete(r); else next.add(r);
    setter(next);
  };

  // Bound how long a revealed password / reset message stays on screen: the
  // the action just taken, never linger while the user moves on to a different row or context.
  const clearBanners = () => { setRevealed(null); setResetMsg(null); };
  
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
  }, []);

  return (
    <div className="space-y-4 p-4">
      {portalNode && createPortal(
        <Link to="/admin" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back
        </Link>,
        portalNode
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Logins &amp; access</h1>
        <Button aria-label="New service account" onClick={() => { clearBanners(); setCreating(true); }}>
          <Plus className="mr-2 h-4 w-4" /> Add Login
        </Button>
      </div>

      <Input
        value={q}
        onChange={(e) => { clearBanners(); setQ(e.target.value); }}
        placeholder="Search email…"
        className="max-w-sm"
      />

      {revealed && <PasswordReveal password={revealed} />}
      {resetMsg && <p className="text-sm text-muted-foreground">{resetMsg}</p>}

      {creating && (
        <div className="space-y-3 rounded border border-border p-3">
          <h2 className="text-sm font-medium">New service account</h2>
          <div className="space-y-1">
            <Label htmlFor="new-user-email">Email</Label>
            <Input id="new-user-email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
          </div>
          <RoleChecks value={newRoles} onToggle={(r) => toggle(newRoles, setNewRoles, r)} />
          <div className="flex gap-2">
            <Button onClick={() => create.mutate()} disabled={create.isPending || !newEmail}>Create</Button>
            <Button variant="outline" onClick={() => { clearBanners(); setCreating(false); }}>Cancel</Button>
          </div>
          {create.isError && <p className="text-sm text-destructive">Couldn&apos;t create account. Please try again.</p>}
        </div>
      )}

      {usersQ.isLoading && <p className="text-sm text-muted-foreground">Loading logins…</p>}
      {usersQ.isError && <p className="text-sm text-destructive">Couldn&apos;t load logins. Please try again.</p>}

      {usersQ.data && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3">Employee</th>
              <th className="py-2 pr-3">Roles</th>
              <th className="py-2 pr-3">Locked</th>
              <th className="py-2 pr-3">Last login</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {usersQ.data.items.map((row) => (
              <tr key={row.id} className="border-b border-border align-top">
                <td className="py-2 pr-3">{row.email}</td>
                <td className="py-2 pr-3">
                  {row.employeeName ? (
                    <Link to={`/admin/employees/${row.employeeId}`} className="text-primary underline">
                      {row.employeeName}
                    </Link>
                  ) : (
                    "— service account"
                  )}
                </td>
                <td className="py-2 pr-3">{row.roles.join(", ") || "—"}</td>
                <td className="py-2 pr-3">{row.lockedOut ? "Yes" : "No"}</td>
                <td className="py-2 pr-3">{row.lastLoginUtc ? new Date(row.lastLoginUtc).toLocaleString() : "—"}</td>
                <td className="py-2">
                  <div className="flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { clearBanners(); setEditing(row); setDraftRoles(new Set(row.roles)); }}
                    >
                      Edit roles
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { clearBanners(); toggleLock.mutate(row); }} disabled={toggleLock.isPending}>
                      {row.lockedOut ? "Unlock" : "Lock"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { clearBanners(); resetPw.mutate(row); }} disabled={resetPw.isPending}>
                      Reset password
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div className="space-y-3 rounded border border-border p-3">
          <h2 className="text-sm font-medium">Edit roles — {editing.email}</h2>
          <RoleChecks value={draftRoles} onToggle={(r) => toggle(draftRoles, setDraftRoles, r)} />
          <div className="flex gap-2">
            <Button onClick={() => setRoles.mutate(editing)} disabled={setRoles.isPending}>Save</Button>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
          {setRoles.isError && <p className="text-sm text-destructive">Couldn&apos;t save roles. Please try again.</p>}
        </div>
      )}
    </div>
  );
}
